import React from "react";
import { Character, SkillName } from "./types";
import { StatCard } from "./StatCard";
import {
	getSkillModifier,
	getSkillAbility,
	getPassivePerception,
	formatSkillName,
} from "./characterUtils";

interface SkillsTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
}

export const SkillsTab: React.FC<SkillsTabProps> = ({
	character,
	updateCharacter,
}) => (
	<div className="space-y-8">
		{/* Skills */}
		<StatCard title="Skills">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				{Object.entries(character.skills).map(([skill, data]) => {
					const skillName = skill as SkillName;
					const modifier = getSkillModifier(character, skillName);
					const ability = getSkillAbility(skillName);
					return (
						<div
							key={skill}
							className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
						>
							<input
								type="checkbox"
								checked={data.proficient}
								onChange={(e) =>
									updateCharacter(
										`skills.${skill}.proficient`,
										e.target.checked
									)
								}
								className="w-5 h-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500"
							/>
							<span className="flex-1 font-medium text-gray-700 capitalize">
								{formatSkillName(skillName)}
							</span>
							<span className="text-xs text-gray-500 uppercase font-semibold">
								({ability.substring(0, 3)})
							</span>
							<span
								className={`font-bold text-lg min-w-[3rem] text-center ${
									modifier >= 0
										? "text-green-600"
										: "text-red-600"
								}`}
							>
								{modifier >= 0 ? "+" : ""}
								{modifier}
							</span>
						</div>
					);
				})}
			</div>
		</StatCard>

		{/* Passive Perception */}
		<StatCard title="Passive Perception">
			<div className="text-center">
				<div className="bg-gradient-to-b from-indigo-100 to-indigo-200 rounded-2xl p-6 border-2 border-indigo-300 inline-block">
					<div className="text-4xl font-black text-indigo-800">
						{getPassivePerception(character)}
					</div>
				</div>
			</div>
		</StatCard>

		{/* Proficiencies & Languages */}
		<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
			<StatCard title="Other Proficiencies">
				<textarea
					value={character.proficiencies}
					onChange={(e) =>
						updateCharacter("proficiencies", e.target.value)
					}
					className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
					rows={5}
					placeholder="Armor, weapons, tools, etc."
				/>
			</StatCard>
			<StatCard title="Languages">
				<textarea
					value={character.languages}
					onChange={(e) =>
						updateCharacter("languages", e.target.value)
					}
					className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
					rows={5}
					placeholder="Common, Elvish, Draconic, etc."
				/>
			</StatCard>
		</div>
	</div>
);
